import React, {
  DragEvent,
  memo,
  RefObject,
  useCallback,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import TextArea from 'core/components/TextArea';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import {
  fieldChangedCSS,
  visitNoteEditorActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';

type Props = {
  editable: boolean;
  sectionRef?: RefObject<HTMLDivElement>;
};

type SectionProps = Props & {
  field: FieldValues;
  isDirty: boolean;
};

function RadiologySectionContent({
  editable,
  sectionRef,
  field,
  isDirty,
}: SectionProps) {
  const textAreaClasses = useMemo(() => ({
    root: clsx({
      [fieldChangedCSS]: isDirty && editable,
    }),
  }), [editable, isDirty]);

  const wrapperClasses = useMemo(() => (clsx({
    '!mt-0': !editable && field.value?.length === 0,
  })), [editable, field.value]);

  const label = useMemo(() => (
    editable || (!editable && (field.value ?? '').length > 0)
      ? 'Review of Imaging'
      : ''
  ), [editable, field.value]);

  const blockEditorDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  }, []);

  return (
    <Wrapper
      id="RadiologyTemplate"
      wrapperRef={sectionRef}
      className={wrapperClasses}
      label={label}
    >
      <TextArea
        {...field}
        trackingLabel="Radiology"
        classes={textAreaClasses}
        id={VisitNoteSOAPSectionNames.RadiologyTemplate}
        disabled={!editable}
        actions={visitNoteEditorActions}
        onDragOver={blockEditorDrop}
      />
    </Wrapper>
  );
}

const RadiologySectionContentMemoized = memo(RadiologySectionContent);

function RadiologySection({
  editable,
  sectionRef,
}: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name="radiology"
      control={control}
      render={({ field, fieldState: { isDirty } }) => (
        <RadiologySectionContentMemoized
          editable={editable}
          sectionRef={sectionRef}
          field={field}
          isDirty={isDirty}
        />
      )}
    />
  );
}

export default memo(RadiologySection);
